import {
  FC,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import cx from 'classnames/bind';
import { motion, AnimatePresence } from 'framer-motion';
import { Pagination, Navigation, EffectFade } from 'swiper';
import { useMediaQuery } from 'react-responsive';
import type { Swiper } from 'swiper/types';

import { Swiper as SwiperComp } from '@/components/Swiper';
import { Button } from '@/components/Button';
import { Tooltip } from '@/components/Tooltip';
import ArrowIcon from '@/assets/arrow.svg';
import useRefs from '@/hooks/useRefs';
import type { IGame } from '@/types/game';

import styles from './SwiperScenario.module.scss';

interface SwiperScenarioProps {
  setStep: (value: number) => void;
  setIdGame: Dispatch<SetStateAction<number>>;
  setNumberQuestion: Dispatch<SetStateAction<number>>;
  scenarios: IGame[];
  idGame: number;
  currentScenarioIndex: number;
  setCurrentScenarioIndex: Dispatch<SetStateAction<number>>;
}

const config = {
  className: 'swiper-game',
  spaceBetween: 50,
  modules: [Navigation, Pagination, EffectFade],
  effect: 'fade',
  fadeEffect: {
    crossFade: true,
  },
};

const cn = cx.bind(styles);

const SwiperScenario: FC<SwiperScenarioProps> = ({
  setStep,
  setIdGame,
  setNumberQuestion,
  scenarios,
  idGame,
  currentScenarioIndex,
  setCurrentScenarioIndex,
}) => {
  const isTablet = useMediaQuery({ query: '(min-width: 834px)' });

  const heightDescription = isTablet ? 87 : 66;

  const [isShowTooltip, setIsShowTooltip] = useState(false);
  const [isShowDescription, setIsShowDescription] = useState<{
    [key: number]: boolean;
  }>({});

  const { refsByKey, setRef } = useRefs();

  useEffect(() => {
    if (scenarios.length) {
      const index = scenarios.findIndex(({ id }) => id === idGame);

      setCurrentScenarioIndex(index);
    }
  }, [scenarios, idGame]);

  useEffect(() => {
    const listRefs = Object.keys(refsByKey).reduce(
      (acc, current) => ({
        ...acc,
        [current]: refsByKey[current]!.offsetHeight > heightDescription,
      }),
      {},
    );

    if (Object.keys(listRefs).length !== 0) {
      setIsShowDescription(listRefs);
    }
  }, [scenarios, heightDescription]);

  const next = useRef(null);
  const prev = useRef(null);
  const paginationRef = useRef(null);

  const refs = {
    next,
    prev,
    pagination: paginationRef,
  };

    const startGame = (isDisabled = false) => {
    if (isDisabled) {
      setIsShowTooltip(true);
    } else {
      setStep(10);
      setNumberQuestion(1);
    }
  };

  const nextScenario = () => {
    const id = scenarios.find(
      (_item, index) => index === currentScenarioIndex + 1,
    )?.id;
    if (id) {
      setIdGame(id);
    }
  };

  const prevScenario = () => {
    const id = scenarios.find(
      (_item, index) => index === currentScenarioIndex - 1,
    )?.id;
    if (id) {
      setIdGame(id);
    }
  };

  const showDescription = (id: number) => {
    setIsShowDescription({ ...isShowDescription, [id]: false });
  };

  const sliderMove = (e: any) => {
    if (e.swipeDirection === 'next') {
      nextScenario();
    }
    if (e.swipeDirection === 'prev') {
      prevScenario();
    }
  };

  const MotionTooltip = motion(Tooltip);
  return (
    <>
      <div className="pagination-game" ref={paginationRef} />
      <SwiperComp
        {...config}
        initialSlide={currentScenarioIndex}
        pagination={{
          renderBullet(index, className) {
            return `<span class="${className} ${
              scenarios[index].achievement === true &&
              scenarios[index].status === true
                ? 'swiper-pagination-bullet-good'
                : ''
            }  ${
              scenarios[index].achievement === false &&
              scenarios[index].status === true
                ? 'swiper-pagination-bullet-bad'
                : ''
            }">${index + 1}</span>`;
          },
        }}
        onSnapIndexChange={(e: Swiper) => sliderMove(e)}
        refs={refs}
      >
        {scenarios.map(
          ({ name, preview_text, id, status, achievement }, index) => (
            <div key={id} className={cn('wrapper')}>
              <span className={cn('title')}>{name}</span>
              <p
                ref={(element) => setRef(element, id)}
                className={cn(isShowDescription[id] ? 'text' : 'text-all')}
              >
                {preview_text}
              </p>
              {isShowDescription[id] && (
                <div className={cn('continuation-wrapper')}>
                  {isShowTooltip && (
                    <AnimatePresence>
                      <MotionTooltip
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        transition={{ duration: 2, ease: 'backInOut' }}
                        onAnimationComplete={() => {
                          setIsShowTooltip(false);
                        }}
                      />
                    </AnimatePresence>
                  )}
                  <button
                    onClick={() => showDescription(id)}
                    className={cn('continuation')}
                    type="button"
                  >
                    Продолжение описания
                  </button>
                </div>
              )}
              <Button
                className={cn(
                  'button',
                  isShowDescription[id] ? 'button-disabled' : '',
                )}
                handleClick={() => startGame(isShowDescription[id])}
                disabled={
                  !achievement &&
                  !scenarios.find((_item, idx) => idx + 1 === index)
                    ?.achievement &&
                  index !== 0
                }
              >
                {status && (
                  <>
                    Пройти еще раз
                    <br />
                    без баллов
                  </>
                )}

                {!achievement &&
                  !scenarios.find((_item, idx) => idx + 1 === index)
                    ?.achievement &&
                  index !== 0 &&
                  'Пройдите предыдущие сценарии'}
                {((!status &&
                  scenarios.find((_item, idx) => idx + 1 === index)
                    ?.achievement) ||
                  (!status && index === 0)) &&
                  'Начать сценарий'}
              </Button>
            </div>
          ),
        )}
      </SwiperComp>
      <div>
        <button
          type="button"
          className={cn('arrowButton', 'prevButton')}
          ref={prev}
          onClick={prevScenario}
        >
          <img
            src={ArrowIcon}
            className={cn('arrowIcon', 'prevIcon')}
            alt="Стрелка налево"
          />
        </button>
        <button
          type="button"
          className={cn('arrowButton', 'nextButton')}
          ref={next}
          onClick={nextScenario}
        >
          <img
            src={ArrowIcon}
            className={cn('arrowIcon')}
            alt="Стрелка направо"
          />
        </button>
      </div>
    </>
  );
};

export default SwiperScenario;
